/**
 * openDocumentView — safely open a stored document in a new browser tab.
 *
 * Browsers block `data:` URI navigation in `target="_blank"` for security
 * (results in about:blank). This helper converts base64 data URIs to an
 * ephemeral Blob URL before calling window.open(), which browsers allow.
 *
 * If the URL is a regular http(s) URL, it opens directly.
 *
 * Used by: PODetailsDrawer, AppointmentDetailDialog
 */
export function openDocumentView(doc: { fileUrl: string; fileName?: string }): void {
    try {
        const url = doc.fileUrl;
        if (!url) throw new Error('No file URL');

        if (url.startsWith('data:')) {
            // Extract MIME and base64 payload
            const [header, b64] = url.split(',');
            const mime = header.match(/data:(.*?);/)?.[1] || 'application/octet-stream';
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const blob = new Blob([bytes], { type: mime });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            // Note: blobUrl is intentionally not revoked — browser needs it until
            // the tab loads. It will be GC'd when the page closes.
        } else {
            // Regular URL (https, relative path) — open directly
            window.open(url, '_blank', 'noopener');
        }
    } catch {
        // toast is not available here; callers should handle the error if needed
        console.error('[openDocumentView] Failed to open document');
    }
}
